import { IInvoiceSettings } from '@monetize/types/app';
import { useEffect } from 'react';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  ExpandableAccordion,
  MBox,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MVStack,
} from '../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../components/Monetize/ExpandCollapseButton';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../constants/settings';
import { useExpandAllControl, useInvoiceSettings } from '../../../../hooks';
import { useDocumentHead } from '../../../../services/documentHead';
import { InvoiceAutomation } from './InvoiceAutomation';
import { InvoiceContactSettings } from './InvoiceContactSettings';
import { InvoiceEmailTiming } from './InvoiceEmailTiming';
import { InvoiceTerms } from './InvoiceTerms';

export const InvoiceSettings = () => {
  const { setDocTitle } = useDocumentHead();

  const { fetchLoading, invoiceSettings, doUpdateInvoiceSettings } =
    useInvoiceSettings();

  const {
    isAllExpanded,
    commandExpanded,
    expandCommandSeq,
    toggleExpandAll,
    onChildExpandChange,
  } = useExpandAllControl();

  useEffect(() => {
    setDocTitle('Settings', 'Invoices');
  }, []);

  /**
   * Handle save and show error message if there is an error
   * Returns boolean if the save was unsuccessful
   */
  const handleSubmit = async (payload: IInvoiceSettings): Promise<void> => {
    try {
      await doUpdateInvoiceSettings(payload);
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Invoices" />
      <MBox w={'100%'} mt={0} overflow="auto" p={1}>
        <MVStack
          maxW={SETTINGS_CONTENT_MAX_WIDTH}
          gap={4}
          alignItems="flex-start"
        >
          {fetchLoading ? (
            <MSkeleton height={200} width="100%" />
          ) : (
            <>
              <ExpandCollapseButton
                isExpanded={isAllExpanded}
                collapsedLabel="Expand all"
                expandedLabel="Collapse all"
                onChange={toggleExpandAll}
              />
              <ExpandableAccordion
                isAllExpanded={commandExpanded}
                expandCommandSeq={expandCommandSeq}
                onExpandChange={onChildExpandChange}
              >
                <InvoiceAutomation
                  invoiceAutomation={invoiceSettings?.invoiceAutomation!}
                  onSubmit={(invoiceAutomation) =>
                    handleSubmit({ ...invoiceSettings!, invoiceAutomation })
                  }
                />
                <InvoiceEmailTiming
                  emailTiming={invoiceSettings?.invoiceEmailingConfig!}
                  onSubmit={(invoiceEmailingConfig) =>
                    handleSubmit({ ...invoiceSettings!, invoiceEmailingConfig })
                  }
                />
                <InvoiceTerms
                  invoiceTerms={invoiceSettings?.invoiceTerms!}
                  onSubmit={(invoiceTerms) =>
                    handleSubmit({ ...invoiceSettings!, invoiceTerms })
                  }
                />
                <InvoiceContactSettings
                  invoiceCustomizationSettings={
                    invoiceSettings?.invoiceCustomizationSettings
                  }
                  onSubmit={(invoiceCustomizationSettings) =>
                    handleSubmit({
                      ...invoiceSettings!,
                      invoiceCustomizationSettings,
                    })
                  }
                />
              </ExpandableAccordion>
            </>
          )}
        </MVStack>
      </MBox>
    </MPageContainer>
  );
};
